import React, { ReactNode, useState } from 'react';
import { Alert, Button, Card, Space, Spin, Splitter, Typography } from 'antd';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

import DocumentFrame from './DocumentFrame';
import { downloadFromUrl, VIEWER_BACKDROP } from './previewKind';

const { Text } = Typography;

/** Collapsed height of the bottom pane - its header bar and nothing else. */
const DETAIL_HEADER_PX = 32;

/** What sits under the document: OCR output, extracted fields, anything. */
export interface DetailPane {
  /** Uppercase caption in the collapse bar. */
  label: string;
  icon?: ReactNode;
  /** Secondary text beside the label, e.g. a field count or a type trail. */
  note?: ReactNode;
  content: ReactNode;
}

export interface DocumentViewerProps {
  /** A signed read URL. Null renders the empty state. */
  url: string | null;
  /** True while that URL is being minted. */
  isLoading?: boolean;
  /** Drives both the title and what the frame decides to draw. */
  fileName?: string;
  /** Preferred over the filename when deciding what to draw. */
  contentType?: string | null;
  /**
   * Omit for no close control.
   *
   * ⚠️ One optional callback rather than the `setSasUrl` + `setSelectedDocument`
   * + `hideClose` trio this replaced. Those let a caller mount a close button
   * that closed nothing, or hide one it had wired - states with no meaning.
   */
  onClose?: () => void;
  /** Bottom collapsible pane. Omit for a viewer that is only the document. */
  detail?: DetailPane;
  /**
   * Shown when there is no URL and nothing is loading.
   *
   * ⚠️ Say only what is true for YOUR documents. The default names no storage
   * product on purpose: these files live in Drive for some callers and in Azure
   * for others, and telling the second group to go and look in Drive sends them
   * somewhere the document has never been.
   */
  emptyDescription?: string;
  /** Default true whenever there is a URL. */
  showDownload?: boolean;
}

/**
 * The document viewer every screen shares: a file above, an optional pane of
 * whatever was extracted from it below.
 */
const DocumentViewer: React.FC<DocumentViewerProps> = ({
  url,
  isLoading = false,
  fileName,
  contentType,
  onClose,
  detail,
  emptyDescription = "A preview isn't available for this document yet. It may still be processing - try again once it finishes.",
  showDownload = true,
}) => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  /* Closed is `['1', 32]`: the document takes everything left over and the bar
   * keeps its fixed height. Open splits 70/30. */
  const [sizes, setSizes] = useState<(number | string)[]>([
    '1',
    DETAIL_HEADER_PX,
  ]);

  const toggleDetail = () => {
    const next = !isDetailOpen;
    setIsDetailOpen(next);
    setSizes(next ? ['70%', '30%'] : ['1', DETAIL_HEADER_PX]);
  };

  return (
    <Card
      size="small"
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      styles={{ body: { padding: 0, flex: 1, overflow: 'hidden' } }}
      title={
        fileName ? (
          <Text
            ellipsis={{ tooltip: fileName }}
            style={{ maxWidth: 320, fontSize: 13 }}
          >
            {fileName}
          </Text>
        ) : undefined
      }
      extra={
        <Space size="small">
          {showDownload && url && (
            <Button
              size="small"
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => downloadFromUrl(url, fileName)}
            >
              Download
            </Button>
          )}
          {onClose && (
            <Button size="small" type="text" onClick={onClose}>
              Close Preview
            </Button>
          )}
        </Space>
      }
    >
      <Splitter
        layout="vertical"
        style={{ height: '100%' }}
        onResize={setSizes}
      >
        <Splitter.Panel
          style={{
            overflow: 'hidden',
            backgroundColor: VIEWER_BACKDROP,
            position: 'relative',
          }}
        >
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                zIndex: 10,
                backgroundColor: VIEWER_BACKDROP,
              }}
            >
              <Space direction="vertical" align="center">
                <Spin size="large" />
                <Text style={{ color: '#fff' }}>Fetching Secure Link...</Text>
              </Space>
            </div>
          )}

          {!url && !isLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                padding: 40,
              }}
            >
              <Alert
                message="Preview not ready"
                description={emptyDescription}
                type="info"
                showIcon
                style={{ maxWidth: 420 }}
              />
            </div>
          )}

          {/* Keyed on the URL: a new document means a fresh frame, never a
              stale one repainted. */}
          <DocumentFrame
            key={url ?? 'empty'}
            url={url}
            fileName={fileName}
            contentType={contentType}
          />
        </Splitter.Panel>

        {detail && (
          <Splitter.Panel
            size={sizes[1]}
            min={DETAIL_HEADER_PX}
            max="80%"
            style={{
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#fff',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <div
                onClick={toggleDetail}
                style={{
                  padding: '0 16px',
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fafafa',
                  cursor: 'pointer',
                  userSelect: 'none',
                  height: DETAIL_HEADER_PX,
                  // Critical: keeps the bar from disappearing when collapsed.
                  flexShrink: 0,
                }}
              >
                <Space size="middle">
                  {isDetailOpen ? (
                    <CaretDownOutlined style={{ fontSize: 10 }} />
                  ) : (
                    <CaretRightOutlined style={{ fontSize: 10 }} />
                  )}
                  <Space size="small">
                    {detail.icon}
                    <Text
                      strong
                      style={{
                        fontSize: 11,
                        color: '#595959',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {detail.label}
                    </Text>
                    {detail.note && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {detail.note}
                      </Text>
                    )}
                  </Space>
                </Space>
              </div>

              {isDetailOpen && (
                <div
                  style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 12,
                    backgroundColor: '#fff',
                  }}
                >
                  {detail.content}
                </div>
              )}
            </div>
          </Splitter.Panel>
        )}
      </Splitter>
    </Card>
  );
};

export default React.memo(DocumentViewer);
